import { useValue } from "@tldraw/state-react";
import {
  createBindingId,
  defaultShapeSchemas,
  toRichText,
  type TLArrowBinding,
  type TLArrowShape,
  type TLHandle,
  type TLShape,
  type TLShapeId,
} from "@tldraw/tlschema";
import type { Extensions } from "@tiptap/core";
import type { IndexKey } from "@tldraw/utils";
import { useEditor } from "../../hooks";
import { Box, Vec, type VecLike } from "../../geometry";
import type { PlanroomEditor } from "../../editor";
import type { CanvasEditor } from "../editor";
import { Polyline2d } from "../geometry2d";
import { segmentsIntersect } from "../math";
import { HTMLContainer, ShapeUtil, SVGContainer, type HandleDragInfo, type ResizeInfo } from "../shape-util";
import { defaultExtensions, isEmptyRichText, RichTextLabel } from "./rich-label";
import { ARROW_LABEL_FONT_SIZE, colorOf, dashArray, fontFamily, STROKE_WIDTH, type DashStyle, type SizeStyle } from "./styles";

// Arrows between points or shapes. Bound ends follow their target: the binding
// stores where on the target the arrow points, and the drawn end stops at the
// target's outline unless the binding is exact.

type Terminal = "start" | "end";

export function arrowBindings(editor: PlanroomEditor, arrowId: TLShapeId) {
  const out: Partial<Record<Terminal, TLArrowBinding>> = {};
  for (const r of (editor as unknown as CanvasEditor).store.query.records("binding").get())
    if (r.type === "arrow" && r.fromId === arrowId) out[(r as TLArrowBinding).props.terminal] = r as TLArrowBinding;
  return out;
}

/** Where a terminal points, in page space, before clipping to its target. */
function anchorPage(editor: PlanroomEditor, arrow: TLArrowShape, terminal: Terminal, binding?: TLArrowBinding) {
  const target = binding && editor.getShape(binding.toId);
  if (!binding || !target) return Vec.From(editor.getShapePageTransform(arrow).applyToPoint(arrow.props[terminal]));
  const bounds = editor.getShapeGeometry(target).bounds;
  const local = binding.props.isPrecise
    ? { x: bounds.minX + binding.props.normalizedAnchor.x * bounds.w, y: bounds.minY + binding.props.normalizedAnchor.y * bounds.h }
    : bounds.center;
  return Vec.From(editor.getShapePageTransform(target).applyToPoint(local));
}

/** Entry point of the segment from→to into a target's outline (page space). */
function clipToOutline(editor: PlanroomEditor, target: TLShape, from: Vec, to: Vec): Vec {
  const outline = (editor as unknown as CanvasEditor).getShapePageOutline(target);
  let best: Vec | null = null,
    bestDist = Infinity;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i],
      b = outline[(i + 1) % outline.length];
    if (!segmentsIntersect(from, to, a, b)) continue;
    const p = lineIntersection(from, to, a, b);
    if (p && Vec.Dist(from, p) < bestDist) {
      best = p;
      bestDist = Vec.Dist(from, p);
    }
  }
  return best ?? to;
}
function lineIntersection(p1: VecLike, p2: VecLike, p3: VecLike, p4: VecLike) {
  const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
  if (!d) return null;
  const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / d;
  return new Vec(p1.x + t * (p2.x - p1.x), p1.y + t * (p2.y - p1.y));
}

export type ArrowInfo = { start: Vec; end: Vec; middle: Vec; path: Vec[]; isArc: boolean; isElbow: boolean };

/** The arrow's resolved geometry in its own shape space. */
export function arrowInfo(editor: PlanroomEditor, arrow: TLArrowShape): ArrowInfo {
  const bindings = arrowBindings(editor, arrow.id);
  let start = anchorPage(editor, arrow, "start", bindings.start);
  let end = anchorPage(editor, arrow, "end", bindings.end);
  const startTarget = bindings.start && editor.getShape(bindings.start.toId);
  const endTarget = bindings.end && editor.getShape(bindings.end.toId);
  const gap = STROKE_WIDTH[arrow.props.size as SizeStyle] * 2;
  if (endTarget && bindings.end && !bindings.end.props.isExact) end = pullBack(clipToOutline(editor, endTarget, start, end), start, gap);
  if (startTarget && bindings.start && !bindings.start.props.isExact)
    start = pullBack(clipToOutline(editor, startTarget, end, start), end, gap);
  const a = Vec.From(editor.getPointInShapeSpace(arrow, start)),
    b = Vec.From(editor.getPointInShapeSpace(arrow, end));
  if (arrow.props.kind === "elbow") {
    const t = arrow.props.elbowMidPoint ?? 0.5;
    const mx = a.x + (b.x - a.x) * t;
    const path = [a, new Vec(mx, a.y), new Vec(mx, b.y), b];
    return { start: a, end: b, middle: new Vec(mx, (a.y + b.y) / 2), path, isArc: false, isElbow: true };
  }
  const bend = arrow.props.bend;
  const mid = Vec.Lrp(a, b, 0.5);
  const len = Vec.Dist(a, b) || 1;
  const normal = new Vec(-(b.y - a.y) / len, (b.x - a.x) / len);
  const middle = mid.add(normal.mul(bend));
  if (Math.abs(bend) < 0.5) return { start: a, end: b, middle, path: [a, b], isArc: false, isElbow: false };
  return { start: a, end: b, middle, path: arcPoints(a, middle, b), isArc: true, isElbow: false };
}
const pullBack = (p: Vec, toward: Vec, distance: number) => {
  const d = Vec.Dist(p, toward);
  return d > distance * 2 ? Vec.Lrp(p, toward, distance / d) : p;
};

/** Points along the circle through a, m and b. */
function arcPoints(a: Vec, m: Vec, b: Vec, n = 32): Vec[] {
  const center = circumcenter(a, m, b);
  if (!center) return [a, b];
  const r = Vec.Dist(center, a);
  const angle = (p: Vec) => Math.atan2(p.y - center.y, p.x - center.x);
  let a0 = angle(a),
    a1 = angle(b);
  const am = angle(m);
  // Sweep through m.
  const within = (x: number, from: number, to: number) => {
    const norm = (v: number) => ((v % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return norm(x - from) <= norm(to - from);
  };
  if (!within(am, a0, a1)) [a0, a1] = [a0, a1 - Math.PI * 2 * Math.sign(a1 - a0 || 1)];
  let sweep = a1 - a0;
  if (within(am, a0, a1) && sweep < 0) sweep += Math.PI * 2;
  if (!within(am, a0, a0 + sweep)) sweep -= Math.PI * 2;
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = a0 + (sweep * i) / n;
    return new Vec(center.x + Math.cos(t) * r, center.y + Math.sin(t) * r);
  });
}
function circumcenter(a: Vec, b: Vec, c: Vec) {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) return null;
  const sq = (p: Vec) => p.x * p.x + p.y * p.y;
  return new Vec(
    (sq(a) * (b.y - c.y) + sq(b) * (c.y - a.y) + sq(c) * (a.y - b.y)) / d,
    (sq(a) * (c.x - b.x) + sq(b) * (a.x - c.x) + sq(c) * (b.x - a.x)) / d,
  );
}

function headPath(kind: string, tip: Vec, from: Vec, size: number): { d: string; filled: boolean } | null {
  if (kind === "none") return null;
  const dir = Vec.Sub(tip, from);
  const len = dir.len() || 1;
  const u = new Vec(dir.x / len, dir.y / len),
    n = new Vec(-u.y, u.x);
  const back = tip.sub(u.mul(size)),
    l = back.add(n.mul(size * 0.5)),
    r = back.sub(n.mul(size * 0.5));
  const P = (p: Vec) => `${p.x},${p.y}`;
  switch (kind) {
    case "arrow":
      return { d: `M${P(l)}L${P(tip)}L${P(r)}`, filled: false };
    case "triangle":
      return { d: `M${P(l)}L${P(tip)}L${P(r)}Z`, filled: true };
    case "inverted": {
      const base = tip.sub(u.mul(size));
      return { d: `M${P(tip.add(n.mul(size * 0.5)))}L${P(base)}L${P(tip.sub(n.mul(size * 0.5)))}Z`, filled: true };
    }
    case "dot":
      return { d: `M${tip.x - size / 3},${tip.y}a${size / 3},${size / 3} 0 1 0 ${(size * 2) / 3},0a${size / 3},${size / 3} 0 1 0 ${(-size * 2) / 3},0`, filled: true };
    case "diamond": {
      const mid = tip.sub(u.mul(size / 2)),
        end = tip.sub(u.mul(size));
      return { d: `M${P(tip)}L${P(mid.add(n.mul(size / 3)))}L${P(end)}L${P(mid.sub(n.mul(size / 3)))}Z`, filled: true };
    }
    case "square": {
      const s = size / 2;
      const c = tip.sub(u.mul(s));
      const pts = [c.add(u.mul(s)).add(n.mul(s)), c.add(u.mul(s)).sub(n.mul(s)), c.sub(u.mul(s)).sub(n.mul(s)), c.sub(u.mul(s)).add(n.mul(s))];
      return { d: `M${pts.map(P).join("L")}Z`, filled: true };
    }
    case "bar":
    case "pipe":
      return { d: `M${P(tip.add(n.mul(size / 2)))}L${P(tip.sub(n.mul(size / 2)))}`, filled: false };
    default:
      return { d: `M${P(l)}L${P(tip)}L${P(r)}`, filled: false };
  }
}

/** The point at fraction t along a polyline, and its direction. */
function along(path: Vec[], t: number) {
  const lengths = path.slice(1).map((p, i) => Vec.Dist(path[i], p));
  const total = lengths.reduce((s, l) => s + l, 0) || 1;
  let target = total * t;
  for (let i = 0; i < lengths.length; i++) {
    if (target <= lengths[i] || i === lengths.length - 1)
      return Vec.Lrp(path[i], path[i + 1], lengths[i] ? Math.min(1, target / lengths[i]) : 0);
    target -= lengths[i];
  }
  return path[0];
}

export class ArrowShapeUtil extends ShapeUtil<TLArrowShape> {
  static override type = "arrow";
  static override props = defaultShapeSchemas.arrow.props;
  extensions: Extensions = defaultExtensions;
  getDefaultProps(): TLArrowShape["props"] {
    return {
      kind: "arc",
      labelColor: "black",
      color: "black",
      fill: "none",
      dash: "draw",
      size: "m",
      arrowheadStart: "none",
      arrowheadEnd: "arrow",
      font: "draw",
      start: { x: 0, y: 0 },
      end: { x: 2, y: 0 },
      bend: 0,
      richText: toRichText(""),
      labelPosition: 0.5,
      scale: 1,
      elbowMidPoint: 0.5,
    } as TLArrowShape["props"];
  }
  getGeometry(shape: TLArrowShape) {
    return new Polyline2d({ points: arrowInfo(this.editor, shape).path });
  }
  override getIndicatorPath(shape: TLArrowShape) {
    const { path } = arrowInfo(this.editor, shape);
    return `M${path.map((p) => `${p.x},${p.y}`).join("L")}`;
  }
  override canBind() {
    return false;
  }
  override canEdit() {
    return true;
  }
  override hideResizeHandles() {
    return true;
  }
  override hideRotateHandle() {
    return true;
  }
  override hideSelectionBoundsBg() {
    return true;
  }
  override getText(shape: TLArrowShape) {
    return isEmptyRichText(shape.props.richText) ? "" : "label";
  }
  override getHandles(shape: TLArrowShape): TLHandle[] {
    const info = arrowInfo(this.editor, shape);
    const handle = (id: string, p: Vec, type: TLHandle["type"], index: string): TLHandle =>
      ({ id, type, x: p.x, y: p.y, index: index as IndexKey }) as TLHandle;
    return [
      handle("start", info.start, "vertex", "a0"),
      handle("middle", info.middle, "virtual", "a2"),
      handle("end", info.end, "vertex", "a3"),
    ];
  }
  override onHandleDrag(shape: TLArrowShape, info: HandleDragInfo<TLArrowShape>) {
    const editor = this.editor as unknown as CanvasEditor;
    const point = new Vec(info.handle.x, info.handle.y);
    if (info.handle.id === "middle") {
      if (shape.props.kind === "elbow") {
        const { start, end } = arrowInfo(editor, shape);
        const t = (point.x - start.x) / ((end.x - start.x) || 1);
        return { id: shape.id, type: "arrow", props: { elbowMidPoint: Math.min(1, Math.max(0, t)) } } as never;
      }
      const { start, end } = arrowInfo(editor, shape);
      const len = Vec.Dist(start, end) || 1;
      const normal = new Vec(-(end.y - start.y) / len, (end.x - start.x) / len);
      const bend = Vec.Sub(point, Vec.Lrp(start, end, 0.5));
      return { id: shape.id, type: "arrow", props: { bend: bend.x * normal.x + bend.y * normal.y } } as never;
    }
    const terminal = info.handle.id as Terminal;
    const page = Vec.From(editor.getShapePageTransform(shape).applyToPoint(point));
    bindTerminal(editor, shape, terminal, page, info.isPrecise);
    return { id: shape.id, type: "arrow", props: { [terminal]: { x: point.x, y: point.y } } } as never;
  }
  override onResize(shape: TLArrowShape, info: ResizeInfo<TLArrowShape>) {
    const scale = (p: VecLike) => ({ x: p.x * info.scaleX, y: p.y * info.scaleY });
    return {
      id: shape.id,
      type: "arrow",
      x: info.newPoint.x,
      y: info.newPoint.y,
      props: { start: scale(shape.props.start), end: scale(shape.props.end), bend: shape.props.bend * Math.abs(info.scaleY) },
    } as never;
  }
  /** Moving an arrow on its own lets go of targets that stay where they are. */
  override onTranslateStart(shape: TLArrowShape) {
    const editor = this.editor as unknown as CanvasEditor;
    const moving = new Set(editor.getSelectedShapeIds());
    const isMoving = (id: TLShapeId) => {
      for (let s = editor.getShape(id); s; s = editor.getShape(s.parentId as TLShapeId)) if (moving.has(s.id)) return true;
      return false;
    };
    const info = arrowInfo(editor, shape);
    const props: Partial<TLArrowShape["props"]> = {};
    for (const binding of Object.values(arrowBindings(editor, shape.id))) {
      if (!binding || isMoving(binding.toId)) continue;
      const point = binding.props.terminal === "start" ? info.start : info.end;
      props[binding.props.terminal] = { x: point.x, y: point.y };
      editor.store.remove([binding.id]);
    }
    return Object.keys(props).length ? ({ id: shape.id, type: "arrow", props } as never) : undefined;
  }
  /** Before a bound target is deleted, freeze the end where it is drawn. */
  onBindingTargetDelete(arrow: TLArrowShape, binding: TLArrowBinding) {
    const info = arrowInfo(this.editor, arrow);
    const point = binding.props.terminal === "start" ? info.start : info.end;
    this.editor.updateShape({ id: arrow.id, type: "arrow", props: { [binding.props.terminal]: { x: point.x, y: point.y } } } as never);
  }
  component(shape: TLArrowShape) {
    return <ArrowBody shape={shape} util={this} />;
  }
}

/** Bind (or unbind) an arrow end to the shape under a page point. */
export function bindTerminal(editor: CanvasEditor, arrow: TLArrowShape, terminal: Terminal, page: Vec, precise = false) {
  const existing = arrowBindings(editor, arrow.id)[terminal];
  const other = arrowBindings(editor, arrow.id)[terminal === "start" ? "end" : "start"];
  const target = editor.getShapeAtPoint(page, {
    hitInside: true,
    margin: 4 / editor.getZoomLevel(),
    filter: (s) =>
      s.id !== arrow.id &&
      s.type !== "arrow" &&
      editor.getUtil(s).canBind?.({ fromShapeType: "arrow", toShapeType: s.type, bindingType: "arrow" }) !== false &&
      !editor.isShapeHidden(s),
  });
  if (!target || (other && other.toId === target.id && !precise)) {
    if (existing) editor.store.remove([existing.id]);
    return;
  }
  const bounds = editor.getShapeGeometry(target).bounds;
  const local = editor.getPointInShapeSpace(target, page);
  const normalizedAnchor = {
    x: Math.min(1, Math.max(0, (local.x - bounds.minX) / (bounds.w || 1))),
    y: Math.min(1, Math.max(0, (local.y - bounds.minY) / (bounds.h || 1))),
  };
  // Near the centre, point at the centre; otherwise at the exact spot.
  const isPrecise = precise || Vec.Dist(normalizedAnchor, { x: 0.5, y: 0.5 }) > 0.2;
  const types = editor.store.schema.types as unknown as Record<string, { create(r: object): TLArrowBinding }>;
  const binding = types.binding.create({
    id: existing?.id ?? createBindingId(),
    type: "arrow",
    fromId: arrow.id,
    toId: target.id,
    props: { terminal, normalizedAnchor, isExact: false, isPrecise, snap: "none" },
    meta: {},
  });
  editor.store.put([binding]);
}

function ArrowBody({ shape, util }: { shape: TLArrowShape; util: ArrowShapeUtil }) {
  const editor = useEditor();
  const info = useValue("arrow info", () => arrowInfo(editor, shape), [editor, shape]);
  const editing = useValue("editing arrow", () => editor.getEditingShapeId() === shape.id, [editor, shape.id]);
  const color = colorOf(editor, shape.props.color).solid;
  const width = STROKE_WIDTH[shape.props.size as SizeStyle] * shape.props.scale;
  const head = Math.max(10, width * 4);
  const path = info.path;
  const d = `M${path.map((p) => `${p.x},${p.y}`).join("L")}`;
  const endHead = headPath(shape.props.arrowheadEnd, info.end, path[Math.max(0, path.length - 2)], head);
  const startHead = headPath(shape.props.arrowheadStart, info.start, path[Math.min(path.length - 1, 1)], head);
  const hasLabel = editing || !isEmptyRichText(shape.props.richText);
  const labelAt = along(path, shape.props.labelPosition ?? 0.5);
  const box = Box.FromPoints(path);
  return (
    <>
      <SVGContainer>
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={width}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashArray(shape.props.dash as DashStyle, width)}
        />
        {[startHead, endHead].map(
          (h, i) =>
            h && (
              <path
                key={i}
                d={h.d}
                fill={h.filled ? color : "none"}
                stroke={color}
                strokeWidth={width}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ),
        )}
      </SVGContainer>
      {hasLabel && (
        <HTMLContainer
          style={{
            position: "absolute",
            left: labelAt.x,
            top: labelAt.y,
            transform: "translate(-50%, -50%)",
            pointerEvents: editing ? "auto" : "none",
            maxWidth: Math.max(160, box.w),
          }}
        >
          <RichTextLabel
            editor={editor}
            shapeId={shape.id}
            type="arrow"
            richText={shape.props.richText}
            editing={editing}
            extensions={util.extensions}
            style={{
              background: "var(--canvas-background, #f7f6f3)",
              padding: "2px 6px",
              fontFamily: fontFamily(editor, shape.props.font),
              fontSize: ARROW_LABEL_FONT_SIZE[shape.props.size as SizeStyle] * shape.props.scale,
              lineHeight: 1.3,
              color: colorOf(editor, shape.props.labelColor).solid,
              textAlign: "center",
              whiteSpace: "pre-wrap",
            }}
          />
        </HTMLContainer>
      )}
    </>
  );
}
